// category-filter-config.abstract.ts

import {WritableSignal} from '@angular/core';
import {AbstractQueryBuilder} from '@global-data';
import {FilterOptions, SelectOption} from '@shared-types';
import {GlobalSearchCategory, GlobalSearchFilterType, IFilterUpdateResult} from '../types/global-search.types';
import {Calendar} from 'primeng/calendar';

export class FilterDefinition<TBuilder extends AbstractQueryBuilder<any, any>, TValue = any> {
	type: GlobalSearchFilterType;
	controlType?: 'select' | 'checkbox' | 'text' | 'date' | 'user-select';
	labelKey: string;
	placeholder?: string;
	icon?: string;
	defaultValue?: TValue | TValue[] | null;
	options?: WritableSignal<SelectOption<TValue>[]>;
	controlOptions?: DeepPartial<{
		styleClass: string;
		SELECT: {
			multi: boolean;
			group: boolean;
			filter: boolean;
			showClear: boolean;
			filterBy: string;
			appendTo: string;
			optionLabel: string;
			optionValue: string;
		};
		DATE: {
			selectionMode: Calendar['selectionMode'];
			appendTo: Calendar['appendTo'];
			readonlyInput: Calendar['readonlyInput'];
			dateFormat: Calendar['dateFormat'];
			showClear: Calendar['showClear'];
			maxDate: Calendar['maxDate'];
			minDate: Calendar['minDate'];
			showIcon: Calendar['showIcon'];
		};
	}>;
	refetchOn?: 'SelectPanelClosed';

	onValueChanged: (value: any) => IFilterUpdateResult<TValue | TValue[] | null>;
	applyFilter: (builder: TBuilder, value: TValue | TValue[] | null) => void;

	constructor(init: Partial<FilterDefinition<TBuilder, TValue>> & Required<Pick<FilterDefinition<TBuilder, TValue>, 'type' | 'labelKey' | 'onValueChanged' | 'applyFilter'>>) {
		this.type = init.type;
		this.controlType = init.controlType;
		this.labelKey = init.labelKey;
		this.placeholder = init.placeholder;
		this.icon = init.icon;
		this.defaultValue = init.defaultValue;
		this.options = init.options;
		this.refetchOn = init.refetchOn;
		this.onValueChanged = init.onValueChanged;
		this.applyFilter = init.applyFilter;
		this.updateControlOptions(init?.controlOptions || {});
	}

	private updateControlOptions(updates: DeepPartial<FilterDefinition<TBuilder, TValue | TValue[] | null>['controlOptions']>) {
		this.controlOptions = {
			styleClass: 'filter-control',
			SELECT: {
				multi: false,
				group: false,
				filter: false,
				showClear: false,
				filterBy: 'label',
				optionLabel: 'label',
				optionValue: 'value',
				...(updates?.SELECT || {}),
			},
			...(updates || {}),
		};
	}
}

export type ICategorySortOptions<T> = FilterOptions<T>;

export abstract class GlobalSearchAbstractCategory<T extends AbstractQueryBuilder<any, any>, SortByType extends string = string, Category extends GlobalSearchCategory = GlobalSearchCategory> {
	public abstract category: Category;

	public abstract getFilters(): FilterDefinition<T, any>[];

	public abstract sortOptions: ICategorySortOptions<SortByType>[];
	constructor() {}
}
